import {useEffect, useRef, useState} from 'react';
import globalDomains from '@/utils/global-domains';
import {
  PLAYER_EVENT,
  PLAYER_SCRIPT_NAME,
  STREAM_EVENT,
} from '@/constants/player/live';
import {useVisibilityChange} from '@/components/common/hooks/useVisibilityChange';

// 모듈 스코프에 cleanup 작업을 관리하는 Promise를 둡니다.
// 이 변수는 컴포넌트가 재마운트 되어도 유지
let cleanupPromise = Promise.resolve();

export const useLivePlayer = ({
  config,
  player,
  playerType,
  autoPlay = false,
  loop = false,
  muted: initMuted = true,
  volume: initVolume = 1,
  onAutoPlayBlock = (event) => {},
  onCanPlay = (event) => {},
  onPlay = (event) => {},
  onPause = (event) => {},
  onDurationChange = (event) => {},
  onTimeUpdate = (event) => {},
  onEnd = (event) => {},
  onError = (event) => {},
  shouldPlayWhenVisible = false,
}) => {
  const videoRef = useRef(null);
  const [isPlayerScriptLoaded, setIsPlayerScriptLoaded] = useState(false);
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);
  const isCanPlay = useRef(false);
  const [playing, setPlayingState] = useState(false);
  //State for checking autoplay on foreground return
  const [wasPlaying, setWasPlaying] = useState(false);
  const [currentTIme, setCurrentTIme] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(initMuted);
  const [volume, setVolume] = useState(initVolume);
  const initConfig = useRef({});

  const [showPoster, setShowPoster] = useState(true);

  const {isGlobalVisible} = useVisibilityChange();

  const playerLib = `${PLAYER_SCRIPT_NAME[playerType]}`;

  const playerScriptUrl = `${globalDomains.STATIC_AFREECATV}/asset/app/liveplayer/player/dist/${playerLib}.js`;

  useEffect(() => {
    // 플레이어 인스턴스를 담을 변수
    let playerInstance = null;

    const initPlayer = async () => {
      // 이전 컴포넌트의 cleanup 작업이 끝날 때까지 기다립니다.
      await cleanupPromise;

      initConfig.current = {
        ...config,
        id: playerType,
        muted,
        volume,
      };
      // 스크립트 로딩 및 플레이어 생성 로직
      let script = document.querySelector(`script[src="${playerScriptUrl}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = playerScriptUrl;
        document.body.appendChild(script);

        // 스크립트 로드를 기다림
        await new Promise((resolve) => {
          script.addEventListener('load', resolve);
        });
      }
      // window[playerLib]가 확실히 로드된 후 플레이어 생성
      if (window[playerLib]) {
        try {
          playerInstance = new window[playerLib](initConfig.current);
          player.current = playerInstance; // ref에 할당
          setIsPlayerScriptLoaded(true); // 이제 이벤트 리스너를 붙일 수 있음
        } catch (error) {
          console.error('Error creating player:', error);
        }
      }
    };

    initPlayer();

    return () => {
      // 이제는 handleLoaded 이벤트를 해제할 필요가 없어짐
      // cleanupPromise를 새로운 비동기 cleanup 작업으로 교체
      cleanupPromise = (async () => {
        if (playerInstance) {
          await playerInstance.close();
        }
      })();
    };
  }, []);

  useEffect(() => {
    if (player.current && isPlayerScriptLoaded) {
      player.current.addEventListener(STREAM_EVENT.CAN_PLAY, (event) => {
        isCanPlay.current = true;
        onCanPlay(event);
      });
      player.current.addEventListener(STREAM_EVENT.PLAY, (event) => {
        setShowPoster(false);
        setPlayingState(true);
        onPlay(event);
      });
      player.current.addEventListener(STREAM_EVENT.PAUSE, (event) => {
        setPlayingState(false);
        //pause가 들어왔다는 것은 재생중이였었다는 것
        setWasPlaying(true);
        onPause(event);
      });
      player.current.addEventListener(STREAM_EVENT.DURATION_CHANGE, (event) => {
        onDurationChange(event);
        setDuration(event.duration);
      });
      player.current.addEventListener(STREAM_EVENT.TIME_UPDATE, (event) => {
        if (isCanPlay.current) {
          onTimeUpdate(event);
        }
      });
      player.current.addEventListener(STREAM_EVENT.VOLUME_CHANGE, (event) => {
        console.warn(event);
      });
      player.current.addEventListener(STREAM_EVENT.END, (event) => {
        if (loop) {
          setPlaying(true);
        } else {
          setPlayingState(false);
          setShowPoster(true);
        }
        onEnd();
      });
      player.current.addEventListener(STREAM_EVENT.ERROR, (event) => {
        onError(event);
      });
      player.current.addEventListener(PLAYER_EVENT.AUTOPLAY_BLOCK, (media) => {
        onAutoPlayBlock(media);
        setShowPoster(true);
      });
      setIsPlayerLoaded(true);
    }
  }, [isPlayerScriptLoaded]);

  useEffect(() => {}, [duration]);

  useEffect(() => {
    if (player.current) {
      player.current.volume = volume;

      if (volume === 0) {
        setMuted(true);
      }
    }
  }, [volume]);

  useEffect(() => {
    if (player.current) {
      player.current.muted = muted;
    }
  }, [muted]);

  useEffect(() => {
    if (shouldPlayWhenVisible) {
      // if (!muted) {
      //return foreground
      if (isGlobalVisible) {
        // setPlaying(wasPlaying);
        setPlaying(true);
      } else {
        //move to background
        setPlaying(false);
      }
      //}
    }
  }, [isGlobalVisible]);

  useEffect(() => {
    playing ? play() : pause();
  }, [playing]);

  const play = () => {
    player.current?.play();
  };

  const pause = () => {
    player.current?.pause();
  };

  const setPlaying = (newPlaying) => {
    //process something...
    setWasPlaying(playing);
    setPlayingState(newPlaying);
  };

  return {
    videoRef,
    isPlayerLoaded,
    isCanPlay,
    playing,
    setPlaying,
    wasPlaying,
    volume,
    setVolume,
    muted,
    setMuted,
    duration,
    setDuration,
    currentTIme,
    setCurrentTIme,
    onAutoPlayBlock,
    onCanPlay,
    onPlay,
    onPause,
    onTimeUpdate,
    showPoster,
  };
};
